import { useState } from 'react'
import type { JSX } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import {
  formatClock,
  formatDayMonth,
  formatDuration,
  formatRunMoment,
  formatTime,
  zoneAbbreviation,
} from '@/lib/format'
import { useBrand } from '@/brand/context'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'

type RunSummary = Schema<'RunSummaryOut'>
type RunDetail = Schema<'RunDetailOut'>
type Step = Schema<'ReasoningStepOut'>
type Stage = Schema<'PassStageOut'>

/**
 * The reasoning trail — S-D5's screen 10, on S-B29's persisted runs (S-B32c).
 *
 * The design's own note is the requirement: *"every run writes the full chain —
 * what it observed, what it concluded, what it did, and what it cost. This is
 * what makes an autonomous agent auditable rather than a black box, and it is
 * the first thing anyone opens when they disagree with an outcome."* So this
 * screen has one job, which is to show a chain in order and hide nothing.
 *
 * **Every run, not every pass.** `GET /seo/runs` was widened in this session
 * (`app/db/runs.history`): a tenant who presses *Run audit now* gets a root run
 * that writes the same audit → decision → action chain a pass's audit stage
 * writes, and a picker that offered only passes would call itself *the*
 * reasoning trail while hiding the reasoning behind every button in the module.
 *
 * **Two lines per step, and the server decides the split.** `title` and `body`
 * arrive already separated off `app/seo/trail.py`, because the convention they
 * are separated on — `label: sentence` — is stated in three modules under
 * `app/seo/` and a fourth copy of it here would be the one that disagreed.
 *
 * **The detail is disclosed, not hidden.** Each step carries the structured
 * evidence for its own sentence, and that is the thing a tenant who disagrees
 * with an outcome actually needs. It sits behind a `<details>` because a
 * fifteen-key object above the next step would push the chain off the screen —
 * the same reason the export exists.
 *
 * **The export is the response, not a rendering of it.** `Export JSON` writes
 * out what the endpoint returned — including each step's stored `summary`,
 * unsplit — so what leaves the product is the row rather than this screen's
 * reading of it.
 *
 * **No rupee badge**, where the prototype shows *₹18.40*. Nothing joins a run
 * to `usage_metering`: it is keyed by tenant, period and metric, and a pass's
 * stages run in-process rather than through the queue so most carry no `job_id`
 * either. The badge therefore states the tokens and the models, which is a
 * figure that reconciles against the rows, and the money stays on the budget
 * burn panel where `app/v1/spend.py` derives it from the ledger that holds it.
 * This is the same call `RunTriggers.tsx` made about the prototype's cap: a
 * number that is not derived from anything is worse than no number.
 */
export function ReasoningTrail(): JSX.Element {
  const brand = useBrand()
  // S-G6. `?run=` is how the approval queue's *See the full reasoning trail →*
  // link arrives — §V2. It is the **initial** selection and not a controlled
  // one: the picker below still sets `runId`, which wins, so following the link
  // and then choosing another run does not fight the URL. A run that is not in
  // the recent list falls back to the newest and says so, rather than silently
  // showing a different article's chain under a link that promised this one's.
  const [params] = useSearchParams()
  const asked = params.get('run')
  const [siteId, setSiteId] = useState<string | null>(null)
  const [runId, setRunId] = useState<string | null>(null)

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const listed = sites.state.data?.sites ?? []
  const selectedSite = siteId ?? listed[0]?.id ?? null

  const runs = useQuery(
    (signal) =>
      selectedSite === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/runs', { params: { query: { site_id: selectedSite } }, signal }),
          ),
    [selectedSite],
  )

  // The newest run until somebody picks another, derived rather than set from an
  // effect — `SearchPerformance`'s reason: an effect would render once with no
  // selection and once with one, and fetch the detail on the second.
  const available = runs.state.data?.items ?? []
  const wanted = runId ?? asked
  const chosen = available.find((run) => run.id === wanted) ?? available[0] ?? null
  const missed = wanted !== null && chosen !== null && chosen.id !== wanted

  const detail = useQuery(
    (signal) =>
      chosen === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/runs/{run_id}', {
              params: { path: { run_id: chosen.id } },
              signal,
            }),
          ),
    [chosen?.id],
  )

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row" style={{ justifyContent: 'space-between' }}>
        <h1 className="ar-h1">Reasoning trail</h1>
        <div className="ar-btn-group">
          {listed.length > 1 ? (
            <select
              className="ar-select"
              style={{ height: 'var(--ar-control-sm)', width: 'auto' }}
              aria-label="Site"
              value={selectedSite ?? ''}
              onChange={(event) => {
                setSiteId(event.target.value)
                // The run belongs to the old site. Keeping it would leave the
                // picker showing this site's runs and the card showing another
                // site's chain, which is the one thing this screen cannot do.
                setRunId(null)
              }}
            >
              {listed.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.domain}
                </option>
              ))}
            </select>
          ) : null}
          {available.length > 0 && chosen !== null ? (
            <RunPicker
              runs={available}
              chosen={chosen}
              onChoose={(id) => {
                setRunId(id)
              }}
            />
          ) : null}
          {detail.state.status === 'ready' && detail.state.data !== null ? (
            <ExportButton run={detail.state.data} />
          ) : null}
        </div>
      </div>

      {sites.state.status === 'loading' ? <Loading label="Loading your sites" lines={3} /> : null}

      {sites.state.status === 'error' ? (
        <div className="ar-card">
          <ErrorState
            failure={sites.state.failure}
            title="Could not load your sites"
            onRetry={sites.refetch}
          />
        </div>
      ) : null}

      {sites.state.status === 'ready' && listed.length === 0 ? (
        <div className="ar-card">
          <Empty
            icon="🌐"
            title="Add a website to get started"
            body={`${brand.productName} writes down what it observed, what it concluded and what it did on every run. Point it at a site and the first run's reasoning appears here.`}
            action={
              <Link className="ar-btn ar-btn--primary" to="/onboarding">
                Add a site
              </Link>
            }
          />
        </div>
      ) : null}

      {missed ? (
        <div className="ar-alert ar-alert--warning" role="status">
          <span className="ar-alert__icon" aria-hidden="true">
            ⚠
          </span>
          <div className="ar-alert__body">
            <strong>That run is not in this site&apos;s recent list</strong>
            The link asked for a run this site has not kept in view — it may belong to another site,
            or be older than the last thirty. The newest run is shown instead.
          </div>
        </div>
      ) : null}

      {selectedSite !== null ? <RunView runs={runs} detail={detail} chosen={chosen} /> : null}
    </div>
  )
}

type RunsQuery = ReturnType<typeof useQuery<Schema<'RunListOut'> | null>>
type DetailQuery = ReturnType<typeof useQuery<RunDetail | null>>

/**
 * The card, or the reason there is not one.
 *
 * The two queries fail apart: a picker that loaded and a detail that did not is
 * a run whose chain could not be read, and it says so beside a picker the
 * tenant can use to try another run — which beats blanking the screen.
 */
function RunView({
  runs,
  detail,
  chosen,
}: {
  runs: RunsQuery
  detail: DetailQuery
  chosen: RunSummary | null
}): JSX.Element | null {
  const brand = useBrand()
  if (runs.state.status === 'loading') return <Loading label="Loading this site's runs" lines={4} />

  if (runs.state.status === 'error') {
    return (
      <div className="ar-card">
        <ErrorState
          failure={runs.state.failure}
          title="Could not load this site's runs"
          onRetry={runs.refetch}
        />
      </div>
    )
  }

  if (chosen === null) {
    return (
      <div className="ar-card">
        <Empty
          icon="🧭"
          title="Nothing has run on this site yet"
          body={`Every audit, keyword check and article ${brand.productName} works on writes down what it observed, what it concluded and what it did. Run one, or set a schedule, and the first chain appears here.`}
          action={
            <Link className="ar-btn ar-btn--primary" to="/seo/triggers">
              Set up when we run
            </Link>
          }
        />
      </div>
    )
  }

  if (detail.state.status === 'loading') {
    return <Loading label="Loading this run's reasoning" lines={6} />
  }

  if (detail.state.status === 'error') {
    return (
      <div className="ar-card">
        <ErrorState
          failure={detail.state.failure}
          title="Could not load this run's reasoning"
          onRetry={detail.refetch}
        />
      </div>
    )
  }

  return detail.state.data === null ? null : (
    <RunCard
      run={detail.state.data}
      onStopped={() => {
        detail.refetch()
        runs.refetch()
      }}
    />
  )
}

/**
 * What `GET /seo/runs` will return at most, from `openapi.json`, where `default`
 * and `maximum` are both 50 — the cap in force even though the call sends no
 * `limit` (WEB-16).
 */
const RUN_LIMIT = 50

/** The picker. One `<option>` per run, named by what it was and when it ran. */
function RunPicker({
  runs,
  chosen,
  onChoose,
}: {
  runs: RunSummary[]
  chosen: RunSummary
  onChoose: (id: string) => void
}): JSX.Element {
  const tenant = useActiveTenant().tenant
  return (
    <select
      className="ar-select"
      style={{ height: 'var(--ar-control-sm)', width: 'auto' }}
      aria-label="Run"
      value={chosen.id}
      onChange={(event) => {
        onChoose(event.target.value)
      }}
    >
      {runs.map((run) => (
        <option key={run.id} value={run.id}>
          {/* *Run · 23 Jul 04:00 IST* (`seo.html#trail`), with the run's own kind
              where the drawing says *Run*: two runs on one day are told apart by
              what they were before by when they ran. */}
          {run.label} · {formatDayMonth(run.created_at, tenant.timezone)}{' '}
          {formatClock(run.created_at, tenant.timezone)} {zoneAbbreviation(tenant.timezone)}
        </option>
      ))}
      {/* WEB-16. The route caps the list and offers no cursor, so a site with
          more than `RUN_LIMIT` runs loses the older ones with no sign of it.
          A trailing disabled option rather than a sentence beside the control:
          this is read at the moment somebody has scrolled to the bottom looking
          for a run that is not there, which is the only moment it matters. */}
      {runs.length >= RUN_LIMIT ? (
        <option disabled>Showing the {RUN_LIMIT} most recent runs</option>
      ) : null}
    </select>
  )
}

/**
 * Export JSON — the response as it arrived, saved to a file.
 *
 * A blob and an object URL rather than a route: the payload is already in the
 * page, a second endpoint would be a second serialisation of the same rows, and
 * this way what is exported is provably what was rendered. The URL is revoked
 * straight after the click, because an object URL lives as long as the document
 * and this button can be pressed once per run for as long as the tab is open.
 */
function ExportButton({ run }: { run: RunDetail }): JSX.Element {
  return (
    <button
      type="button"
      className="ar-btn ar-btn--ghost ar-btn--sm"
      onClick={() => {
        const blob = new Blob([JSON.stringify(run, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        // The short id, which is what the card's own heading shows — a filename
        // carrying a full UUID is one nobody can tell apart at a glance in a
        // downloads folder, which is where these end up.
        link.download = `augrank-run-${run.id.slice(0, 8)}.json`
        link.click()
        URL.revokeObjectURL(url)
      }}
    >
      Export JSON
    </button>
  )
}

/** What each run status is called, and how the badge is coloured. */
const STATUS_TONE: Record<string, { tone: string; label: string }> = {
  queued: { tone: 'ar-badge--neutral', label: 'Queued' },
  running: { tone: 'ar-badge--info', label: 'Running' },
  succeeded: { tone: 'ar-badge--success', label: '✓ Completed' },
  partial: { tone: 'ar-badge--warning', label: '⚠ Partly done' },
  // S-B30. Neither failed nor partial: it finished, and a spend ceiling is why
  // it did less than it was asked for. `0028_run_degraded` argues the split, and
  // the two point a tenant at different screens.
  degraded: { tone: 'ar-badge--warning', label: '⚠ Stopped on budget' },
  failed: { tone: 'ar-badge--danger', label: '✗ Failed' },
  cancelled: { tone: 'ar-badge--neutral', label: 'Stopped' },
  timed_out: { tone: 'ar-badge--danger', label: '✗ Timed out' },
}

/**
 * How the closing dot of a chain is tinted, by the run's status (SEO-UI-103).
 *
 * A separate table from `STATUS_TONE` rather than a reading of it, because the
 * two answer different questions and one of them has a deliberate hole:
 * `succeeded` is **absent here**, so a run that worked ends on the ordinary
 * primary dot. Tinting every terminal dot would make the colour mean *"this is
 * the end"*, which the missing spine line already says, and then the warning
 * would carry no information at all.
 *
 * The words match the badge above them on purpose — a reader who has taken in
 * "Stopped on budget" at the top of the card and then finds an amber dot at the
 * bottom is being told the same thing twice about the same run, which is what
 * makes the second one legible without a legend.
 */
/** The statuses a run cannot leave — the ones whose last step is really the last. */
const FINISHED: ReadonlySet<string> = new Set([
  'succeeded',
  'partial',
  'degraded',
  'cancelled',
  'failed',
  'timed_out',
])

/** The publish target names as the settings screen says them. */
const TARGET_LABELS: Record<string, string> = {
  download: 'files to download',
  git: 'the git repository',
  wordpress: 'WordPress',
  webflow: 'Webflow',
  shopify: 'Shopify',
}

/**
 * Where an automatically approved draft in this run was queued, or `null`.
 *
 * `app/seo/content.py` writes `publishes_to` on its *draft stored* step when
 * the site's settings approved the draft on arrival; a pass that ended at the
 * queue writes `null` there and has nothing published, which is the sentence
 * the last step says.
 */
function publishedTo(trail: Step[]): string | null {
  for (const step of trail) {
    const target = step.detail['publishes_to']
    if (typeof target === 'string' && target !== '') return target
  }
  return null
}

const DOT_TONE: Record<string, string> = {
  partial: 'app-trail__dot--warning',
  degraded: 'app-trail__dot--warning',
  cancelled: 'app-trail__dot--muted',
  failed: 'app-trail__dot--danger',
  timed_out: 'app-trail__dot--danger',
}

/**
 * One run: what it was, how it went, what it cost, and its chain.
 *
 * The heading carries the **short id**, as the prototype does, because that is
 * what a tenant quotes to support and what the exported filename carries.
 */
function RunCard({ run, onStopped }: { run: RunDetail; onStopped: () => void }): JSX.Element {
  const { tenant, membership } = useActiveTenant()
  const status = STATUS_TONE[run.status] ?? { tone: 'ar-badge--neutral', label: run.status }
  // Only a finished run has a duration, and `started_at` is null on one that was
  // cancelled before its task ever began — `app/db/runs.open_pass` opens a pass
  // as `queued` precisely so that "never started" stays distinguishable.
  const took =
    run.started_at !== null && run.finished_at !== null
      ? formatDuration(run.started_at, run.finished_at)
      : null
  // **Stop**, for a pass that has not ended. `POST /seo/runs/{id}/cancel` has
  // existed since S-B29 and no screen called it: a tenant looking at a pass
  // `queued` for four days (the live tenant, 2026-09-15 to 19) had nothing to
  // press. Passes only — a stage's ceiling is its pass's, and the route refuses
  // a child. Gated as *Run now* is, on `discovery:manage`.
  const stoppable = run.kind === 'pass' && run.finished_at === null && !run.cancelling
  const stop = useMutation(() =>
    call(() =>
      api.POST('/api/v1/seo/runs/{run_id}/cancel', { params: { path: { run_id: run.id } } }),
    ),
  )

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>
          {/* The drawing's *Run 7f2a91c — 23 July, 04:00 IST*: a seven-character
              id, and the moment without seconds (parity P-TRAIL-03). */}
          {run.label} {run.id.slice(0, 7)} — {formatRunMoment(run.created_at, tenant.timezone)}
        </h2>
        <span className="ar-card__spacer" />
        <span className={`ar-badge ${status.tone}`}>
          {status.label}
          {took === null ? '' : ` in ${took}`}
        </span>
        {run.usage.metered ? (
          <span className="ar-badge ar-badge--neutral">
            {(run.usage.input_tokens + run.usage.output_tokens).toLocaleString('en-IN')} tokens
            {run.usage.models.length > 0 ? ` · ${run.usage.models.join(', ')}` : ''}
          </span>
        ) : null}
        {stoppable && may(membership, 'discovery:manage') ? (
          <Button
            tone="ghost"
            size="sm"
            busy={stop.pending}
            busyLabel="Stopping"
            onClick={() => {
              void stop.run(undefined).then((result) => {
                if (result.ok) onStopped()
              })
            }}
          >
            Stop this run
          </Button>
        ) : null}
      </div>
      <div className="ar-card__body ar-stack">
        {stop.state.status === 'error' ? <InlineError failure={stop.state.failure} /> : null}
        {run.error !== null ? (
          <div className="ar-alert ar-alert--danger" role="status">
            <span className="ar-alert__icon" aria-hidden="true">
              !
            </span>
            <div className="ar-alert__body">
              <strong>This run did not finish its work</strong>
              {run.error}
            </div>
          </div>
        ) : null}

        {run.stages.length > 0 ? <Stages stages={run.stages} /> : null}

        {run.trail.length === 0 ? (
          <Empty
            icon="⏳"
            title={
              run.finished_at !== null
                ? 'This run wrote no reasoning'
                : run.started_at === null
                  ? 'This run has not started yet'
                  : 'This run has not written anything yet'
            }
            body={
              run.finished_at !== null
                ? 'It ended before it observed anything — the run card above says how. Nothing was changed on your site.'
                : run.started_at === null
                  ? // A queued pass whose task never began. The hourly sweep
                    // ends it once its time limit passes; until then the honest
                    // sentence is that nothing is happening, not "reload".
                    'It is waiting for its task to begin. If it is still waiting past its time limit it is ended automatically within the hour, or you can stop it now.'
                  : 'The chain fills in as the run works. Reload in a moment to see what it has observed so far.'
            }
          />
        ) : (
          <div className="app-trail">
            {run.trail.map((step, index) => (
              <TrailStep
                key={`${step.run_id}:${String(step.step_number)}`}
                step={step}
                timeZone={tenant.timezone}
                last={index === run.trail.length - 1}
                // SEO-UI-103. How the chain **ended**, on the dot that closes
                // it. The design tints its last dot `--ar-warning` under
                // *"Stopped — waiting for approval"*, and the tone is the one
                // thing about a step this screen can derive rather than invent:
                // no step carries a status of its own, but the run does, and
                // the final dot is the only one whose meaning the run's status
                // describes. `succeeded` keeps the primary, so a healthy chain
                // is unchanged and the colour only ever appears where something
                // is worth reading.
                endedAs={index === run.trail.length - 1 ? run.status : null}
                publishedTo={index === run.trail.length - 1 ? publishedTo(run.trail) : null}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * A pass's stages, above its chain.
 *
 * Only a pass has any: a standalone run *is* the work, and the server returns an
 * empty list for one rather than a single stage describing itself. Drawn as
 * badges rather than as a second timeline, because the chain below already runs
 * through all of them in order and two orderings of the same run on one card is
 * one more than a reader can hold.
 */
function Stages({ stages }: { stages: Stage[] }): JSX.Element {
  return (
    <div className="ar-row" style={{ gap: 'var(--ar-space-2)', flexWrap: 'wrap' }}>
      {stages.map((stage) => {
        const status = STATUS_TONE[stage.status] ?? {
          tone: 'ar-badge--neutral',
          label: stage.status,
        }
        return (
          <span key={stage.id} className={`ar-badge ${status.tone}`}>
            {stage.label} · {status.label}
          </span>
        )
      })}
    </div>
  )
}

/**
 * One line of the chain.
 *
 * The spine's connecting line is omitted on the last step, which is what closes
 * the timeline — the prototype does the same, and a line running off the bottom
 * of the card reads as *"and then something we are not showing you"*.
 */
function TrailStep({
  step,
  timeZone,
  last,
  endedAs,
  publishedTo,
}: {
  step: Step
  timeZone: string
  last: boolean
  /** The run's status, on the last step only — see `endedAs` at the call site
   *  (SEO-UI-103). `null` on every other step, which keeps the primary dot. */
  endedAs: string | null
  /** Where an automatically approved draft was queued, on the last step only. */
  publishedTo: string | null
}): JSX.Element {
  const keys = Object.keys(step.detail)
  const tone = endedAs === null ? null : (DOT_TONE[endedAs] ?? null)
  return (
    <div className="app-trail__item">
      <span className="app-trail__time">{formatTime(step.created_at, timeZone)}</span>
      <span className="app-trail__spine" aria-hidden="true">
        <span className={tone === null ? 'app-trail__dot' : `app-trail__dot ${tone}`} />
        {last ? null : <span className="app-trail__line" />}
      </span>
      <div className="app-trail__body">
        {/* The phase alone where the step named nothing. `title` is null in that
            case rather than falling back to the phase's own word, which the
            first build did and which rendered `PassExecutor`'s "Pass finished"
            as *"Acted — Acted"*. */}
        <strong>
          {step.phase_label}
          {step.title === null ? '' : ` — ${step.title}`}
        </strong>
        <span className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          {step.body}
          {/* The drawing closes the chain on *Nothing was published.* A pass
              ends at the approval queue (parity P-TRAIL-02) — unless the tenant
              set the site to publish new posts automatically, in which case the
              queue decided the moment the draft arrived and a publish is on its
              way. The live tenant's 04:00 pass on 2026-09-20 said *Nothing was
              published* under a dashboard that counted one; `publishedTo` is
              read off the draft-stored step's own record. */}
          {last && endedAs !== null && FINISHED.has(endedAs) ? (
            <>
              {' '}
              <strong>
                {publishedTo === null
                  ? 'Nothing was published.'
                  : `One draft was approved automatically and queued for ${TARGET_LABELS[publishedTo] ?? publishedTo}.`}
              </strong>
            </>
          ) : null}
        </span>
        {step.model !== null ? (
          <span
            className="ar-subtle"
            style={{ display: 'block', fontSize: 'var(--ar-text-caption-size)' }}
          >
            {step.model}
            {step.input_tokens === null ? '' : ` · ${step.input_tokens.toLocaleString('en-IN')} in`}
            {step.output_tokens === null
              ? ''
              : ` · ${step.output_tokens.toLocaleString('en-IN')} out`}
          </span>
        ) : null}
        {keys.length > 0 ? (
          <details className="app-trail__detail">
            <summary>
              What this was based on ({keys.length} {keys.length === 1 ? 'field' : 'fields'})
            </summary>
            <pre className="app-trail__json">{JSON.stringify(step.detail, null, 2)}</pre>
          </details>
        ) : null}
      </div>
    </div>
  )
}
